import { AxiosError } from 'axios';
import { HubConnectionState } from '@microsoft/signalr';

// Classifies a Print failure into an actionable popup message + a log-friendly code.
// A failed request with no response is opaque to JS (ad blocker, firewall, CORS, TLS, server
// down all look identical), so we use the live SignalR connection as a reachability hint to
// distinguish "server is reachable, this request was stopped" from "server unreachable".

export type LiveLink = 'up' | 'reconnecting' | 'down' | 'unknown';

export type PrintDiag = {
  /** Short, log-friendly classifier, e.g. 'PRINT-BLOCKED'. */
  code: string;
  /** i18n leaf key under `modals.print.*`. */
  messageKey: string;
};

/**
 * Collapse every hub's connection state into a single reachability signal. Any connected hub
 * means the gateway/BE is reachable from this computer right now.
 */
export function liveLinkFrom(states: Array<HubConnectionState | undefined>): LiveLink {
  const known = states.filter((s): s is HubConnectionState => s !== undefined);
  if (known.some((s) => s === HubConnectionState.Connected)) return 'up';
  if (known.some((s) => s === HubConnectionState.Reconnecting || s === HubConnectionState.Connecting))
    return 'reconnecting';
  if (known.length > 0) return 'down';
  return 'unknown';
}

export function diagnosePrintError(error: unknown, link: LiveLink): PrintDiag {
  const e = error as AxiosError | undefined;

  // 1) We got an HTTP response -> the request reached the backend.
  const status = e?.response?.status;
  if (typeof status === 'number') {
    const messageKey =
      status >= 500 ? 'server' : status === 401 || status === 403 ? 'auth' : 'rejected';
    return { code: `PRINT-HTTP-${status}`, messageKey };
  }

  // 2) Offline (no network interface at all).
  if (!navigator.onLine) {
    return { code: 'PRINT-OFFLINE', messageKey: 'offline' };
  }

  // 3) Timeout (the request has a configured timeout) — also how an unreachable host manifests.
  if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') {
    return { code: 'PRINT-TIMEOUT', messageKey: 'timeout' };
  }

  // 4) Request made but no response. The live socket hints at whether the host is reachable.
  const noResponse =
    e?.code === 'ERR_NETWORK' || e?.message === 'Network Error' || (!!e?.request && !e?.response);
  if (noResponse) {
    if (link === 'up') return { code: 'PRINT-BLOCKED', messageKey: 'blocked' };
    if (link === 'reconnecting') return { code: 'PRINT-LINK-FLAP', messageKey: 'linkFlap' };
    return { code: link === 'down' ? 'PRINT-NETBLOCK' : 'PRINT-NETERR', messageKey: 'netblock' };
  }

  // 5) Anything else: a JS error preparing the request.
  return { code: 'PRINT-JS', messageKey: 'generic' };
}
